'use client';

import React from 'react';
import clsx from 'clsx';
import { Check } from 'lucide-react';
import { StepTwoTopicsProps } from '@/sections/forms/multistep-form/steps/TopicsStep/types';
import { useTranslations } from '@/lib/locale-context';

const TopicsStep: React.FC<StepTwoTopicsProps> = ({
  topics,
  selectedTopics,
  onToggleTopic,
  anliegen,
  onChangeAnliegen,
  hasSelectedConsultation,
}) => {
  const { t } = useTranslations();

  if (!hasSelectedConsultation) {
    return (
      <p className="text-sm text-slate-500">
        {t('header.termin.selectConsultationFirst')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div
        className="grid grid-cols-2 gap-3 sm:gap-4"
        role="group"
        aria-label={t('header.termin.topicHeadline')}
      >
        {topics.map((t, index) => {
          const val = t.administratorTitle;
          const isSelected = selectedTopics.includes(val);
          const inputId = `topic-${t.id}`;
          const isLastOddItem =
            topics.length % 2 !== 0 && index === topics.length - 1;

          return (
            <label
              key={t.id}
              htmlFor={inputId}
              className={clsx('block', isLastOddItem && 'col-span-2')}
            >
              <input
                id={inputId}
                type="checkbox"
                name="topics[]"
                value={val}
                checked={isSelected}
                onChange={() => onToggleTopic(val)}
                className="pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0"
              />

              <div
                role="checkbox"
                aria-checked={isSelected}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    onToggleTopic(val);
                  }
                }}
                className={clsx(
                  'w-full rounded-[12px] border p-4 text-left text-base',
                  'flex items-center justify-between gap-3',
                  'cursor-pointer select-none transition-colors',
                  isSelected
                    ? 'border-brand-700 bg-[#F6FBFF] text-slate-900'
                    : 'border-[#CDD1DB] bg-white text-slate-900 hover:border-brand-300',
                )}
              >
                <span className="flex-1">{val}</span>

                {/* Checkbox Icon */}
                <div
                  className={clsx(
                    'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border-2 transition-colors',
                    isSelected
                      ? 'border-brand-700 bg-brand-700'
                      : 'border-[#CDD1DB] bg-white',
                  )}
                >
                  {isSelected && (
                    <Check className="h-3 w-3 text-white" strokeWidth={3} />
                  )}
                </div>
              </div>
            </label>
          );
        })}
      </div>

      {selectedTopics.map((t) => (
        <input key={t} type="hidden" name={t} value={t} />
      ))}

      <div className="flex flex-col gap-2">
        <label className="text-sm font-semibold text-slate-900">
          {t('header.termin.concernLabel')}
        </label>

        <textarea
          value={anliegen}
          onChange={(e) => onChangeAnliegen(e.target.value)}
          placeholder={t('header.termin.concernPlaceholder')}
          rows={4}
          className={clsx(
            'w-full resize-none rounded-lg border border-neutral-200',
            'px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400',
            'focus:outline-none focus:ring-2 focus:ring-brand-500',
          )}
        />

        {anliegen.trim() && (
          <input type="hidden" name="anliegen" value={anliegen} />
        )}
      </div>
    </div>
  );
};

export default TopicsStep;
